import _, {chain} from 'lodash';

import {ErrorWithData} from '../../../../../libs/shared/helpers/src/types/customErrors';
import {isTracker} from '../../../../../src/helpers/modelHelpers';
import {
  ActivityModel,
  ActivityVisualTypesEnum,
  CommentActivityModel,
  RCommentActivity,
} from '../../../../../src/models/api/activityModel';
import {
  AnnotationHighlightCommentModel,
  AnnotationModel,
  RHighlightCommentAnnotation,
} from '../../../../../src/models/api/annotationModel';
import {
  AttachmentVisualContentTypesEnum,
  isAttachmentModel,
} from '../../../../../src/models/api/attachmentModel';
import {CommentModel} from '../../../../../src/models/api/commentModel';
import {RecipientRolesEnum} from '../../../../../src/models/api/recipientModel';
import {PermissionsEnum} from '../../../../../src/models/api/roleModel';
import {TeammateModel} from '../../../../../src/models/api/teammateModel';
import {AppState} from '../../../../../src/store/appState';
import {getHasPermission} from '../../../../../src/store/data/access/accessSelectors';
import {makeGetVisualRecipients} from '../../../../../src/store/data/contacts/recipientsSelectors';
import {
  ConversationSelector,
  IsTeamConversationSelector,
} from '../../../../../src/store/data/conversations/conversationsSelectors';
import {
  getActivities,
  getAllActivities,
  getComments,
  getConversations,
  getMessages,
  getReferencingCommentUidsMap,
} from '../../../../../src/store/data/dataSelectors';
import {getFindTeammate} from '../../../../../src/store/data/teammates/lazyTeammatesSelectors';
import {getUserId} from '../../../../../src/store/data/teammates/teammatesSelectors';
import {ViewerPanelPageSelector} from '../../../../../src/store/panels/viewer/viewerPanelSelectors';
import {ViewerPanelPageTypesEnum} from '../../../../../src/store/panels/viewer/viewerPanelState';
import {AppParametricSelector, createSelector} from '../../../../helpers/reselectHelpers';
import {reportToBugsnag} from '../../../../services/bugsnag/bugsnag';

export function getCommentActivity(_state: AppState, props: {activity: CommentActivityModel}) {
  return props.activity;
}

export function getPreviousActivity(_state: AppState, props: {previousActivity: ActivityModel | undefined}) {
  return props.previousActivity;
}

interface ConversationIdSelectorProps {
  conversationId: number;
}
type ConversationIdSelector = AppParametricSelector<ConversationIdSelectorProps, number>;
export const getConversationId: ConversationIdSelector = (_state, props) => props.conversationId;

/** Find the comment for the current activity. */
type GetCommentSelector = AppParametricSelector<any, CommentModel | null>;
export function makeGetComment() {
  return createSelector([getComments, getCommentActivity], (comments, activity) => {
    // Find the comment for this activity.
    const comment = comments[activity.commentUid];
    if (!comment) {
      reportToBugsnag(new ErrorWithData('A comment could not be found for this activity.', {activity}));
      return null;
    }
    return comment;
  });
}

/** Find the previous comment, if any. */
export function makeGetPreviousComment() {
  return createSelector([getComments, getPreviousActivity], (comments, previousActivity) => {
    // We may not have a previous activity, or it may not be a comment.
    if (!previousActivity || previousActivity.visualType !== ActivityVisualTypesEnum.COMMENT) {
      return undefined;
    }

    // If one was found, fine the corresponding comment.
    return comments[previousActivity.commentUid];
  });
}

/** Find the author for our comment. */
export function makeGetAuthorFromComment(getComment: GetCommentSelector) {
  return createSelector([getFindTeammate, getComment], (findTeammate, comment) => {
    if (!comment) {
      return undefined;
    }
    const author = findTeammate(comment.authorId, 'commentViewerSelectors makeGetAuthorFromComment');
    if (!author) {
      reportToBugsnag(new Error('An author for the specified comment could not be found.'));
      return undefined;
    }
    return author;
  });
}

type GetPinnedByTeammateId = AppParametricSelector<any, number | undefined>;
export function makeGetPinnedByTeammate(getPinnedByTeammateId: GetPinnedByTeammateId) {
  return createSelector([getFindTeammate, getPinnedByTeammateId], (findTeammate, pinnedByTeammateId) =>
    pinnedByTeammateId
      ? findTeammate(pinnedByTeammateId, 'commentViewerSelectors makeGetPinnedByTeammate')
      : undefined,
  );
}

/** Find the referenced activity for the supplied comment. */
type GetReferencedActivitySelector = AppParametricSelector<any, ActivityModel | undefined>;
export function makeGetReferencedActivity(getComment: GetCommentSelector) {
  return createSelector([getComment, getAllActivities], (comment, activities) => {
    if (!comment?.referencedActivityId) {
      return undefined;
    }

    const activity = activities[comment.referencedActivityId];
    if (!activity) {
      reportToBugsnag(new Error('A referenced activity for the specified comment could not be found.'));
      return undefined;
    }

    // We only support referenced comment and message activities currently.
    if (
      activity.visualType !== ActivityVisualTypesEnum.COMMENT &&
      activity.visualType !== ActivityVisualTypesEnum.MESSAGE
    ) {
      return undefined;
    }

    return activity;
  });
}

/** Find the author referenced in this comment. */
export function makeGetReferencedAuthor(getReferencedActivity: GetReferencedActivitySelector) {
  return createSelector([getReferencedActivity, getFindTeammate], (referencedActivity, findTeammate) => {
    if (
      !referencedActivity ||
      referencedActivity.visualType !== ActivityVisualTypesEnum.COMMENT ||
      !referencedActivity.authorId
    ) {
      return undefined;
    }

    const author = findTeammate(
      referencedActivity.authorId,
      'commentViewerSelectors makeGetReferencedAuthor',
    );
    if (!author) {
      reportToBugsnag(new Error('A referenced author for the specified activity could not be found.'));
      return undefined;
    }

    return author;
  });
}

/** Find the comment referenced in this comment. */
export function makeGetReferencedComment(getReferencedActivity: GetReferencedActivitySelector) {
  return createSelector([getReferencedActivity, getComments], (referencedActivity, comments) => {
    if (
      !referencedActivity ||
      referencedActivity.visualType !== ActivityVisualTypesEnum.COMMENT ||
      referencedActivity.isTombstoned
    ) {
      // There is no activity referenced or the activity has been tombstoned.
      return undefined;
    }

    if (referencedActivity.deletedAt) {
      // The referenced activity has been deleted.
      return undefined;
    }

    return comments[referencedActivity.commentUid];
  });
}

/** Find the recipients referenced in this comment. */
export function makeGetReferencedRecipient(getActivity: GetReferencedActivitySelector) {
  const getMessage = createSelector([getActivity, getMessages], (activity, messages) => {
    if (!activity || activity.visualType !== ActivityVisualTypesEnum.MESSAGE) {
      return undefined;
    }

    return messages[activity.messageUid];
  });

  const getNamespace = createSelector([getMessage, getConversations], (message, conversations) => {
    const conversation = message && conversations[message.conversationId];
    return (conversation && conversation.namespace) || 'global';
  });

  const getVisualRecipients = makeGetVisualRecipients(getNamespace, getMessage);

  return createSelector([getVisualRecipients], (visualRecipients) =>
    visualRecipients.find((r) => r.role === RecipientRolesEnum.FROM),
  );
}

/** Cast the attachments from our comment. */
export function makeGetAttachments(getComment: GetCommentSelector) {
  return createSelector([getComment], (comment) => {
    if (!comment) {
      return [];
    }
    return chain(comment.attachments)
      .map((a) => isAttachmentModel(a) && a)
      .compact()
      .sortBy([
        (a) => (a.visualContentType === AttachmentVisualContentTypesEnum.IMAGE ? 0 : 1),
        (a) => a.attachedAt ?? 0,
      ])
      .value();
  });
}

export function makeGetHasSameAuthorAsPrevious(getComment: GetCommentSelector) {
  return createSelector(
    [getComment, makeGetPreviousComment(), getPreviousActivity],
    (comment, previousComment, previousActivity) => {
      if (!comment) {
        return false;
      }
      const previousCommentAuthorId = previousComment && previousComment.authorId;
      // If the previous activity is tombstoned, check for the activity's authorId instead.
      const previousCommentActivityAuthorId =
        previousActivity &&
        previousActivity.visualType === ActivityVisualTypesEnum.COMMENT &&
        previousActivity.isTombstoned &&
        previousActivity.authorId;
      return (
        previousCommentAuthorId === comment.authorId || previousCommentActivityAuthorId === comment.authorId
      );
    },
  );
}

export function makeGetIsReferencingTheSameMessageAsPrevious(getComment: GetCommentSelector) {
  return createSelector([getComment, makeGetPreviousComment()], (comment, previousComment) => {
    if (!comment) {
      return false;
    }
    return (
      previousComment?.referencedActivityId === comment.referencedActivityId &&
      Boolean(previousComment?.annotation) &&
      Boolean(comment.annotation)
    );
  });
}

export function makeGetIsFromUser(getComment: GetCommentSelector) {
  return createSelector([getUserId, getComment], (userId, comment) => {
    if (!comment) {
      return false;
    }
    return userId === comment.authorId;
  });
}

export interface CommentReactionVisualProps {
  createdAt: number;
  emojiString: string;
  isUserSelected?: boolean;
  teammates: ReadonlyArray<TeammateModel>;
}

export function makeGetReactionsVisualProps(getComment: GetCommentSelector) {
  return createSelector(
    [getUserId, getComment, getFindTeammate],
    (userId, comment, findTeammate): CommentReactionVisualProps[] => {
      if (!comment) {
        return [];
      }
      return _(comment.reactions)
        .groupBy((reaction) => reaction.emojiString)
        .map((reaction) => {
          const firstReaction = _(reaction).first();

          const teammateIds = _(reaction)
            .map((r) => r.teammateId)
            .uniq()
            .value();

          if (!firstReaction || teammateIds.length < 1) {
            return undefined;
          }

          return {
            emojiString: firstReaction.emojiString,
            createdAt: firstReaction.groupCreatedAt,
            isUserSelected: teammateIds.includes(userId),
            teammates: _(teammateIds)
              .map((i) => findTeammate(i, 'commentViewerSelectors makeGetReactionsVisualProps'))
              .compact()
              .sortBy((i) => i.id === userId)
              .value(),
          };
        })
        .compact()
        .sortBy((r) => r.createdAt)
        .value();
    },
  );
}

export function makeGetConversation<P extends ConversationIdSelectorProps>(): ConversationSelector<P> {
  return createSelector([getConversationId, getConversations], (conversationId, conversations) => {
    const conversation = conversations[conversationId];
    if (!conversation) {
      throw new Error('A conversation could not be found for the specified ID.');
    }

    return conversation;
  });
}

export function makeGetCanAddReactions<P>(getConversation: ConversationSelector<P>) {
  return createSelector([getHasPermission, getConversation], (hasPermission, conversation) =>
    hasPermission(conversation.namespace, PermissionsEnum.CONVERSATION_COMMENT),
  );
}

export function makeGetIsVisibleOnlyToUser<P>(
  getConversation: ConversationSelector<P>,
  getIsTeamConversation: IsTeamConversationSelector<P>,
) {
  return createSelector([getConversation, getIsTeamConversation], (conversation, isTeamConversation) => {
    if (isTeamConversation || !conversation) {
      return false;
    }

    const conversationTrackers = _(conversation.trackers)
      .filter((t) => isTracker(t))
      .compact()
      .value();

    return !conversationTrackers.length || conversationTrackers.length === 1;
  });
}

export function makeGetIsEditing<P>(
  getComment: GetCommentSelector,
  getContentPage: ViewerPanelPageSelector<P>,
) {
  return createSelector([getComment, getContentPage], (comment, contentPage) => {
    if (!comment || contentPage?.type !== ViewerPanelPageTypesEnum.SINGLE_CONVERSATION) {
      return false;
    }

    const {editingCommentUid} = contentPage;
    return editingCommentUid === comment.uid;
  });
}

export function makeGetHighlightAnnotations(getActivityId: AppParametricSelector<any, number>) {
  return createSelector(
    [getActivityId, getActivities, getComments, getReferencingCommentUidsMap],
    (activityId, activities, comments, referencingCommentUidsMap) => {
      const referencingCommentUids = (activityId && referencingCommentUidsMap[activityId]) || [];

      return _(referencingCommentUids)
        .map((uid) => {
          const annotatingComment = comments[uid];
          if (
            annotatingComment &&
            annotatingComment.annotation &&
            isHighlightCommentAnnotation(annotatingComment.annotation)
          ) {
            const {referencedActivityId} = annotatingComment;
            const referencedActivity =
              referencedActivityId === undefined ? undefined : activities[referencedActivityId];
            const annotatedComment = isCommentActivity(referencedActivity)
              ? comments[referencedActivity.commentUid]
              : undefined;
            const originalWasEdited =
              annotatedComment?.lastEditedAt !== undefined &&
              annotatingComment?.annotation?.createdAt !== undefined &&
              annotatedComment.lastEditedAt >= annotatingComment.annotation.createdAt;
            return originalWasEdited ? undefined : annotatingComment.annotation;
          }

          return undefined;
        })
        .compact()
        .sortBy((annotation) => annotation.startIndex)
        .value();
    },
  );
}

function isHighlightCommentAnnotation(
  annotation: AnnotationModel,
): annotation is AnnotationHighlightCommentModel {
  return RHighlightCommentAnnotation.is(annotation);
}

function isCommentActivity(activity: ActivityModel | undefined): activity is CommentActivityModel {
  return activity !== undefined && RCommentActivity.is(activity);
}
